import {
  ComponentsSchema,
  type Component,
  type ComponentKind,
  type ComponentSuggestion,
} from '@ai-secure-twin/shared';

/** How each component kind is named in the console. */
export const COMPONENT_KIND_LABEL: Record<ComponentKind, string> = {
  WEB_FRONTEND: 'Web front end',
  API: 'API',
  AUTHENTICATION: 'Sign-in / identity',
  ADMIN: 'Admin',
  STATIC_CONTENT: 'Static content',
  FILE_STORE: 'File store',
  DATA_STORE: 'Data store',
  THIRD_PARTY: 'Third-party service',
  OTHER: 'Other',
};

export const COMPONENT_KINDS = Object.keys(COMPONENT_KIND_LABEL) as ComponentKind[];

/**
 * A component as the form holds it: hosts and prefixes as the text a person types. Converted to
 * the shared shape only on submit, and validated there by the same schema the server uses.
 */
export interface ComponentDraft {
  id: string;
  name: string;
  kind: ComponentKind;
  description: string;
  hosts: string;
  pathPrefixes: string;
  connectsTo: string[];
}

const splitList = (text: string) =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

export function toDraft(component: Component): ComponentDraft {
  return {
    id: component.id,
    name: component.name,
    kind: component.kind,
    description: component.description ?? '',
    hosts: component.hosts.join(', '),
    pathPrefixes: component.pathPrefixes.join(', '),
    connectsTo: [...component.connectsTo],
  };
}

export function newDraft(partial: Partial<ComponentDraft> = {}): ComponentDraft {
  return {
    id: crypto.randomUUID(),
    name: '',
    kind: 'WEB_FRONTEND',
    description: '',
    hosts: '',
    pathPrefixes: '',
    connectsTo: [],
    ...partial,
  };
}

/** A suggestion from the surface map, as a draft a person can rename before saving. */
export function draftFromSuggestion(
  suggestion: ComponentSuggestion,
  existingNames: readonly string[],
): ComponentDraft {
  const taken = new Set(existingNames.map((n) => n.toLowerCase()));
  let name = suggestion.name;
  for (let i = 2; taken.has(name.toLowerCase()); i += 1) name = `${suggestion.name} (${i})`;
  return newDraft({
    name,
    kind: suggestion.kind,
    hosts: suggestion.host,
    pathPrefixes: suggestion.pathPrefix ?? '',
  });
}

export type DraftResult = { ok: true; components: Component[] } | { ok: false; errors: string[] };

/**
 * The drafts as components, checked by the shared schema — the same rules the API applies, so
 * a mistake is shown in the form rather than discovered as a 400. Edges to removed components
 * are dropped first: removing a component should not leave the form unsavable.
 */
export function fromDrafts(drafts: readonly ComponentDraft[]): DraftResult {
  const ids = new Set(drafts.map((d) => d.id));
  const parsed = ComponentsSchema.safeParse(
    drafts.map((d) => ({
      id: d.id,
      name: d.name,
      kind: d.kind,
      ...(d.description.trim() ? { description: d.description } : {}),
      hosts: splitList(d.hosts),
      pathPrefixes: splitList(d.pathPrefixes),
      connectsTo: d.connectsTo.filter((id) => ids.has(id)),
    })),
  );
  if (parsed.success) return { ok: true, components: parsed.data };
  return {
    ok: false,
    errors: parsed.error.issues.map((issue) => {
      const [index, field] = issue.path;
      const name = typeof index === 'number' ? drafts[index]?.name || `Component ${index + 1}` : '';
      return [name, field ? String(field) : '', issue.message].filter(Boolean).join(' · ');
    }),
  };
}
